/*
 * File: DrawerLinkItems.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Displays links in drawer/sidebar
 */
import React from "react";
import { Link } from "react-router-dom";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import LogoutHandler from "services/login/handlers/LogoutHandler";
import PropTypes from "prop-types";
import AssignmentTurnedIn from "@material-ui/icons/AssignmentTurnedIn";
import DateRangeIcon from "@material-ui/icons/DateRange";
import PersonIcon from "@material-ui/icons/Person";
import PeopleIcon from "@material-ui/icons/People";
import DashboardIcon from "@material-ui/icons/Dashboard";
import SettingsIcon from "@material-ui/icons/Settings";
import IconInput from "@material-ui/icons/Input";
import HelpIcon from "@material-ui/icons/Help";
import MainDrawerPatientLink from "./MainDrawerPatientLink";
import { makeStyles } from "@material-ui/core";
import PatientId from "services/patients/store/PatientIdStore";

const mainNav = [
  {
    title: "Dashboard",
    icon: <DashboardIcon />,
    url: "/dashboard"
  },
  {
    isPatient: true,
    title: "Patients",
    icon: <PersonIcon />,
    url: "/"
  },
  {
    title: "Schedule",
    icon: <DateRangeIcon />,
    url: "/appointments"
  },
  {
    title: "Orders",
    icon: <AssignmentTurnedIn />,
    url: "/orders"
  },
  {
    title: "Referrers",
    icon: <PeopleIcon />,
    url: "/referrals"
  },
  {
    title: "Preferences",
    icon: <SettingsIcon />,
    url: "/preferences"
  },
  {
    title: "Help",
    icon: <HelpIcon />,
    url: "/help"
  }
];

const useStyles = makeStyles(theme => ({
  link: {
    color: `${theme.palette.common.white} !important`
  }
}));

const DrawerLinkItems = ({ handleDrawerToggle }) => {
  const classes = useStyles();
  const patientId = PatientId.getId();
  const navLinks = mainNav.map((item, idx) => {
    // patient links
    if (item.isPatient) {
      // patient has been selected so show the patient sublinks
      if (patientId) {
        return (
          <MainDrawerPatientLink
            patientId={patientId}
            item={item}
            key={idx}
            handleDrawerToggle={handleDrawerToggle}
          />
        );
      }

      // just return a patient search link
      return (
        <Link
          to="/search"
          key={idx}
          className={classes.link}
          id="patient-link-search"
        >
          <ListItem button onClick={handleDrawerToggle}>
            <ListItemIcon className={classes.link}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Patients" />
          </ListItem>
        </Link>
      );
    }

    // other links
    let id = "sidebar-link-" + item.url.replace("/", "");
    return (
      <Link to={item.url} key={idx} className={classes.link} id={id}>
        <ListItem button onClick={handleDrawerToggle}>
          <ListItemIcon className={classes.link}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.title} />
        </ListItem>
      </Link>
    );
  });

  return (
    <div>
      {navLinks}

      {/* Logout link */}
      <LogoutHandler>
        <ListItem button className={classes.link} id="sidebar-link-logout">
          <ListItemIcon className={classes.link}>
            <IconInput />
          </ListItemIcon>
          <ListItemText primary="Log Out" />
        </ListItem>
      </LogoutHandler>
    </div>
  );
};

DrawerLinkItems.propTypes = {
  handleDrawerToggle: PropTypes.func.isRequired
};

export default DrawerLinkItems;
